import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { Combobox } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Checkbox, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useWriteOffCandidates,
  useWriteOffs,
  writeOffTotals,
  type WriteOff,
} from '@/lib/accountant';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import { homeCurrency } from '@/lib/org-locale';

type Tab = 'open' | 'history';

export const Route = createFileRoute('/accounting/write-off-invoices')({
  component: WriteOffInvoices,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'history' ? { tab: 'history' } : {},
});

const OVERDUE: Array<[number, string]> = [
  [0, 'Any open invoice'],
  [1, 'Overdue'],
  [30, 'More than 30 days overdue'],
  [60, 'More than 60 days overdue'],
  [90, 'More than 90 days overdue'],
  [180, 'More than 180 days overdue'],
];

/** Screen A9 (document 06): Write off invoices, QBO Accountant tools. */
function WriteOffInvoices() {
  const { tab = 'open' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const orgId = useOrgId();
  const tabClass = (on: boolean) =>
    cn(
      '-mb-px border-b-2 pb-2 text-sm',
      on
        ? 'border-primary font-medium'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    );
  if (!orgId) return <EmptyState title="Select a client" />;
  return (
    <div>
      <PageHeader
        title="Write off invoices"
        description="Write the unpaid balance of invoices off to a bad-debt account. Each write-off settles its invoice and can be undone."
      />
      <div className="mb-4 flex items-center gap-6 border-b" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'open'}
          className={tabClass(tab === 'open')}
          onClick={() => void navigate({ search: {} })}
        >
          Invoices to write off
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'history'}
          className={tabClass(tab === 'history')}
          onClick={() => void navigate({ search: { tab: 'history' } })}
        >
          Written off
        </button>
      </div>
      {tab === 'history' ? <History /> : <Candidates />}
    </div>
  );
}

function Candidates() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const accounts = useAccounts();
  const customers = useContacts({ kind: 'customer' });
  const [asOf, setAsOf] = useState(todayIso());
  const [minDays, setMinDays] = useState(1);
  const [contactId, setContactId] = useState<string | null>(null);
  const rows = useWriteOffCandidates({
    asOf,
    minDaysOverdue: minDays,
    ...(contactId ? { contactId } : {}),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [relief, setRelief] = useState<Set<string>>(new Set());
  const [date, setDate] = useState(todayIso());
  const [accountId, setAccountId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [notified, setNotified] = useState(false);

  const expenseAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => a.isActive && a.class === 'expense' && !a.isControl),
    [accounts.data],
  );
  useEffect(() => {
    if (!accountId && expenseAccounts.length)
      setAccountId(
        (expenseAccounts.find((a) => a.detailType === 'bad_debts') ?? expenseAccounts[0])!.id,
      );
  }, [expenseAccounts, accountId]);
  useEffect(() => setDate(asOf), [asOf]);

  const list = rows.data ?? [];
  const totals = writeOffTotals(list, selected, relief);
  const toggle = (set: Set<string>, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  };
  const allOn = list.length > 0 && list.every((r) => selected.has(r.documentId));

  const write = useMutation({
    mutationFn: (reason: string) =>
      api<WriteOff[]>(`/orgs/${orgId}/write-offs`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          badDebtAccountId: accountId,
          reason,
          invoices: [...selected].map((documentId) => ({
            documentId,
            claimVatRelief: relief.has(documentId),
          })),
        }),
      }),
    onSuccess: async (done) => {
      await qc.invalidateQueries();
      setSelected(new Set());
      setRelief(new Set());
      setConfirming(false);
      toast(
        `${done.length} invoice${done.length === 1 ? '' : 's'} written off. See them under Written off.`,
      );
    },
  });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
        <Field label="As of" className="w-40">
          {(id) => <DateInput id={id} value={asOf} onChange={setAsOf} />}
        </Field>
        <Field label="Show" className="w-64">
          {(id) => (
            <NativeSelect
              id={id}
              value={minDays}
              onChange={(e) => setMinDays(Number(e.target.value))}
            >
              {OVERDUE.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Customer" className="w-72">
          {() => (
            <Combobox
              ariaLabel="Customer"
              items={(customers.data ?? []).map((c) => ({ id: c.id, label: c.displayName }))}
              value={contactId}
              placeholder="All customers"
              onChange={setContactId}
            />
          )}
        </Field>
      </div>

      {rows.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.isError ? (
        <EmptyState title="Could not load invoices" hint={rows.error.message} />
      ) : !list.length ? (
        <EmptyState title="No invoices to write off" hint="Nothing open matches these filters." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  {isStaff ? (
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      className="size-4 accent-primary"
                      checked={allOn}
                      onChange={() =>
                        setSelected(allOn ? new Set() : new Set(list.map((r) => r.documentId)))
                      }
                    />
                  ) : null}
                </TableHead>
                <TableHead>Invoice no.</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Invoice date</TableHead>
                <TableHead>Due date</TableHead>
                <TableHead className="text-right">Days overdue</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Open balance</TableHead>
                <TableHead className="text-right">VAT in balance</TableHead>
                <TableHead>VAT relief</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((r) => (
                <TableRow key={r.documentId} data-testid={`candidate-${r.number}`}>
                  <TableCell>
                    {isStaff ? (
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        aria-label={`Select ${r.number}`}
                        checked={selected.has(r.documentId)}
                        onChange={() => setSelected(toggle(selected, r.documentId))}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/sales/invoices/$documentId"
                      params={{ documentId: r.documentId }}
                      className="text-primary hover:underline"
                    >
                      {r.number}
                    </Link>
                  </TableCell>
                  <TableCell>{r.contactName}</TableCell>
                  <TableCell>{formatDate(r.issueDate)}</TableCell>
                  <TableCell>{r.dueDate ? formatDate(r.dueDate) : ''}</TableCell>
                  <TableCell className="tnum text-right">{Math.max(0, r.daysOverdue)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(r.total)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(r.balance)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(r.vatShare)}</TableCell>
                  <TableCell>
                    <span title={r.relief.eligible ? undefined : r.relief.reasons.join('. ')}>
                      <Checkbox
                        label={r.relief.eligible ? 'Claim' : 'Not eligible'}
                        disabled={!isStaff || !r.relief.eligible || !selected.has(r.documentId)}
                        checked={relief.has(r.documentId) && selected.has(r.documentId)}
                        onChange={() => setRelief(toggle(relief, r.documentId))}
                      />
                    </span>
                    {!r.relief.eligible && r.relief.reasons.length ? (
                      <span className="block text-xs text-muted-foreground">
                        {r.relief.reasons[0]}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {isStaff && selected.size ? (
        <div
          className="sticky bottom-0 mt-3 flex flex-wrap items-end gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm"
          data-testid="write-off-bar"
        >
          <Field label="Write-off date" className="w-40">
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
          <Field label="Bad-debt account" className="w-72">
            {(id) => (
              <NativeSelect
                id={id}
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {expenseAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.fullName}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <div className="ml-auto text-right text-sm">
            <p>
              {totals.count} invoice{totals.count === 1 ? '' : 's'} ·{' '}
              <span className="tnum font-semibold">
                {homeCurrency()} {formatMinor(totals.amount)}
              </span>
            </p>
            {totals.relief > 0n ? (
              <p className="tnum text-muted-foreground">
                VAT relief claimed {homeCurrency()} {formatMinor(totals.relief)}
              </p>
            ) : null}
          </div>
          <Button
            onClick={() => {
              setNotified(false);
              setConfirming(true);
            }}
            disabled={!accountId}
          >
            Write off
          </Button>
        </div>
      ) : null}

      <ReasonDialog
        open={confirming}
        title={`Write off ${totals.count} invoice${totals.count === 1 ? '' : 's'}?`}
        description={
          <>
            {homeCurrency()} {formatMinor(totals.amount - totals.relief)} goes to the bad-debt
            account
            {totals.relief > 0n
              ? ` and ${homeCurrency()} ${formatMinor(totals.relief)} of VAT relief to VAT control, with an adjustment on the open VAT return`
              : ''}
            ; A/R is credited and each invoice is settled. You can undo a write-off later.
          </>
        }
        confirmLabel="Write off"
        pending={write.isPending}
        error={
          write.error?.message ??
          (totals.relief > 0n && !notified
            ? 'Confirm the customer has been told before claiming VAT relief.'
            : null)
        }
        onConfirm={(reason) => {
          if (totals.relief > 0n && !notified) return;
          write.mutate(reason);
        }}
        onClose={() => {
          write.reset();
          setConfirming(false);
        }}
      >
        {totals.relief > 0n ? (
          <Checkbox
            label="The customer has been notified that the amount is written off (needed for VAT relief)"
            checked={notified}
            onChange={(e) => setNotified(e.target.checked)}
          />
        ) : null}
      </ReasonDialog>
    </>
  );
}

function History() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const list = useWriteOffs();
  const [undoing, setUndoing] = useState<WriteOff | null>(null);
  const undo = useMutation({
    mutationFn: ({ w, reason }: { w: WriteOff; reason: string }) =>
      api<WriteOff>(`/orgs/${orgId}/write-offs/${w.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (w) => {
      await qc.invalidateQueries();
      setUndoing(null);
      toast(`Write-off of ${w.number} undone; the invoice is open again.`);
    },
  });
  if (list.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (list.isError)
    return <EmptyState title="Could not load write-offs" hint={list.error.message} />;
  if (!list.data.length)
    return <EmptyState title="Nothing written off yet" hint="Write-offs you post appear here." />;
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">VAT relief</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-24">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.data.map((w) => (
            <TableRow key={w.id}>
              <TableCell>{formatDate(w.date)}</TableCell>
              <TableCell>
                <Link
                  to="/sales/invoices/$documentId"
                  params={{ documentId: w.documentId }}
                  className="text-primary hover:underline"
                >
                  {w.number}
                </Link>
              </TableCell>
              <TableCell>{w.contactName}</TableCell>
              <TableCell className="tnum text-right">
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: w.journalEntryId }}
                  className="text-primary hover:underline"
                >
                  {formatAmount(w.amount)}
                </Link>
              </TableCell>
              <TableCell className="tnum text-right">
                {w.vatRelief !== '0.00' ? formatAmount(w.vatRelief) : ''}
                {w.vatReturnPeriod ? (
                  <span className="block text-xs text-muted-foreground">
                    Return {w.vatReturnPeriod}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{w.badDebtAccount}</TableCell>
              <TableCell className="max-w-[240px] truncate" title={w.reason}>
                {w.reason}
              </TableCell>
              <TableCell>
                {w.status === 'posted' ? (
                  <Badge variant="secondary">Posted</Badge>
                ) : (
                  <span title={w.reverseReason ?? undefined}>
                    <Badge variant="outline">Undone</Badge>
                  </span>
                )}
              </TableCell>
              <TableCell>
                {isStaff && w.status === 'posted' ? (
                  <Button variant="link" size="sm" className="px-1" onClick={() => setUndoing(w)}>
                    Undo
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ReasonDialog
        open={undoing !== null}
        title={`Undo the write-off of ${undoing?.number ?? ''}?`}
        description="The write-off entry is reversed on its own date, the invoice is open again and any VAT relief adjustment is removed from the VAT return."
        confirmLabel="Undo write-off"
        destructive
        pending={undo.isPending}
        error={undo.error?.message ?? null}
        onConfirm={(reason) => undoing && undo.mutate({ w: undoing, reason })}
        onClose={() => {
          undo.reset();
          setUndoing(null);
        }}
      />
    </div>
  );
}
